import { claimsApi } from "./api/claimsApi";
import { parseOfficeCodes, resolveClaimOfficeCode } from "./claimsOfficeUtils";
import {
  formToCreateRequest,
  formToUpdateRequest,
  getApiErrorMessage,
  isDraftClaim,
  mapDetailsDto,
} from "./claimsMappers";
import { parseCurrencyValue } from "./claimFormUtils";

const resolveOfficeForClaim = ({ form, existingClaim, officeCode }) =>
  resolveClaimOfficeCode({
    shipmentOfficeCode: form?.shipmentOfficeCode,
    claimOfficeCode: existingClaim?.officeCode,
    officeCode,
  });

export const loadClaimForEdit = async (claimNumber, officeCode, preferredOfficeCode) => {
  const codes = preferredOfficeCode
    ? [preferredOfficeCode]
    : parseOfficeCodes(officeCode);
  if (codes.length === 0) {
    throw new Error("Office code is required to load this claim.");
  }

  let lastError;
  for (const code of codes) {
    try {
      const [details, documents] = await Promise.all([
        claimsApi.getClaim(claimNumber, code),
        claimsApi.getClaimDocuments(claimNumber, code),
      ]);
      return {
        ...mapDetailsDto(details, documents),
        officeCode: code,
      };
    } catch (error) {
      lastError = error;
      if (![403, 404].includes(error?.response?.status)) {
        throw error;
      }
    }
  }

  throw lastError || new Error("Claim not found.");
};

export const syncClaimDocuments = async ({
  claimNumber,
  officeCode,
  previousDocuments = [],
  nextDocuments = [],
}) => {
  const previousById = new Map(
    (previousDocuments || [])
      .filter((doc) => doc.isExisting && (doc.documentId ?? doc.id))
      .map((doc) => [String(doc.documentId ?? doc.id), doc])
  );
  const nextExistingIds = new Set(
    (nextDocuments || [])
      .filter((doc) => doc.isExisting && (doc.documentId ?? doc.id))
      .map((doc) => String(doc.documentId ?? doc.id))
  );

  for (const [documentId] of previousById.entries()) {
    if (!nextExistingIds.has(documentId)) {
      await claimsApi.deleteClaimDocument(claimNumber, documentId, officeCode);
    }
  }

  const uploads = (nextDocuments || []).filter((doc) => doc.file);
  for (const doc of uploads) {
    await claimsApi.uploadClaimDocument(claimNumber, doc.file, officeCode);
  }
};

export const saveClaim = async ({
  form,
  officeCode,
  existingClaim = null,
  mode = "standalone",
  submit = false,
  draft = false,
}) => {
  const resolvedOfficeCode = resolveOfficeForClaim({ form, existingClaim, officeCode });
  if (!resolvedOfficeCode) {
    throw new Error("Unable to determine office code for this claim.");
  }

  const createPayload = formToCreateRequest(form, { parseCurrencyValue });
  const updatePayload = formToUpdateRequest(form, {
    parseCurrencyValue,
    includeStatus: Boolean(existingClaim && !isDraftClaim(existingClaim)),
  });

  let savedClaim;

  if (existingClaim?.claimNumber) {
    savedClaim = await claimsApi.updateClaim(
      existingClaim.claimNumber,
      updatePayload,
      resolvedOfficeCode
    );
  } else {
    savedClaim = await claimsApi.createClaim(
      form.shipmentNumber,
      createPayload,
      resolvedOfficeCode
    );
  }

  const claimNumber = savedClaim.claimNumber;

  const documentOfficeCode =
    existingClaim?.officeCode || savedClaim?.officeCode || resolvedOfficeCode;

  await syncClaimDocuments({
    claimNumber,
    officeCode: documentOfficeCode,
    previousDocuments: existingClaim?.supportingDocuments || [],
    nextDocuments: form.supportingDocuments || [],
  });

  if (submit) {
    savedClaim = await claimsApi.submitClaim(
      claimNumber,
      existingClaim?.officeCode || resolvedOfficeCode
    );
  }

  const documents = await claimsApi.getClaimDocuments(claimNumber, resolvedOfficeCode);
  return {
    ...mapDetailsDto(savedClaim, documents),
    officeCode: resolvedOfficeCode,
  };
};

export { getApiErrorMessage };
